/* MANGO MOJITO pairing deck (sections/mm-pairing-reel.liquid; Option B, 27 Oct 2026, in place of the two reels).
   A deck of cards, each one shoe worn with one look, in the section's shoe order with each shoe's looks best first.
   The arrows, a swipe or the arrow keys turn it, and the front card always leaves the way the arrow points or the
   finger moves: right shows the next pairing, left the previous one; the deck wraps round. The card behind takes the
   new picture first, then rises into place. On a shoe's own page (data-compact) the deck holds only that shoe's
   looks and the caption names the look. The pairings and picture names come from assets/mm-pairing-reel.json
   (export_theme.py); the shoes, titles, prices and links from the section's products. A custom element, so the theme
   editor's section load and unload start and stop it. */
(() => {
  if (customElements.get('mm-pairing-reel')) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const LEAVE = 420;            // ms before the deck redraws; the card transition itself is 460ms (mm-pairing-reel.css)
  const SWIPE = 60, FLICK = 0.5; // px to turn the deck, or px per ms for a quick flick of 20px or more
  const pad = (n) => String(n).padStart(2, '0');
  const fill = (s, o) => Object.keys(o).reduce((t, k) => t.split(`[${k}]`).join(o[k]), s);

  class MmPairingReel extends HTMLElement {
    connectedCallback() {
      this.ctrl = new AbortController();
      this.load().catch((e) => { if (e.name !== 'AbortError') { this.hidden = true; console.warn('mm-pairing-reel', e); } });
    }

    disconnectedCallback() {
      if (this.ctrl) this.ctrl.abort();
      clearTimeout(this.timer);
      if (this.deck) this.deck.replaceChildren();
      this.items = null;
    }

    async load() {
      const read = (sel) => JSON.parse(this.querySelector(sel).textContent);
      const picked = read('[data-shoes]');
      this.text = read('[data-text]');
      const src = this.dataset.src;
      const res = await fetch(src, { signal: this.ctrl.signal });
      if (!res.ok) throw new Error(`${res.status} ${src}`);
      const data = await res.json();
      if (!this.isConnected) return;

      const base = src.split('?')[0].replace(/[^/]+$/, '');
      const widths = data.widths.slice().sort((a, b) => a - b);
      // On a product page the section lists its pictures with the theme's own asset URLs (the same the first cards in
      // the page use), so nothing is fetched twice; elsewhere they are built from the data file's folder.
      const own = this.querySelector('[data-pictures]');
      const urls = own ? JSON.parse(own.textContent) : {};
      this.srcset = (name) => (urls[name] ? urls[name].srcset : widths.map((w) => `${base}${name}-${w}.webp?v=${data.version} ${w}w`).join(', '));
      this.fallback = (name) => (urls[name] ? urls[name].src : `${base}${name}-${widths[0]}.webp?v=${data.version}`);
      const lang = (this.dataset.locale || 'en').split('-')[0];
      const say = (o) => (o && (o[lang] || o.en)) || '';

      // One card per pairing: the picked shoes in the editor's order, each with its looks best first.
      const looks = new Map(data.looks.map((l) => [l.id, say(l.name)]));
      const occasions = new Map(data.looks.map((l) => [l.id, say(l.occasion)]));
      const pictures = new Map(data.pairings.map((p) => [`${p.look}|${p.shoe}`, p.image]));
      const shoeLooks = new Map(data.shoes.map((s) => [s.handle, s.looks]));
      this.items = picked.flatMap((shoe) => {
        const list = (shoeLooks.get(shoe.handle) || []).filter((id) => looks.has(id) && pictures.has(`${id}|${shoe.handle}`));
        return list.map((id, rank) => ({ shoe, look: looks.get(id), occasion: occasions.get(id), image: pictures.get(`${id}|${shoe.handle}`), rank, of: list.length }));
      });
      if (!this.items.length) { this.hidden = true; return; }

      this.deck = this.querySelector('[data-deck]');
      this.index = Math.max(0, this.items.findIndex((it) => it.shoe.handle === this.dataset.start));
      this.busy = false;
      // One pairing has nothing to turn to.
      this.querySelector('[data-controls]').hidden = this.items.length < 2;
      this.bind();
      this.render();
    }

    mod(i) { return ((i % this.items.length) + this.items.length) % this.items.length; }

    // Each card sits 10px higher and 5% smaller per step back; the third is half faded.
    slot(depth) { return `translateY(${-depth * 10}px) scale(${1 - depth * 0.05})`; }

    picture(img, it, front) {
      img.sizes = '(max-width: 420px) 74vw, 300px';
      img.srcset = this.srcset(it.image);
      img.src = this.fallback(it.image);
      img.alt = front ? fill(this.text.pair_alt, { title: it.shoe.title, look: it.look }) : '';
    }

    card(it, depth) {
      const el = document.createElement('div');
      el.className = 'mm-reel__card';
      el.style.transform = this.slot(depth);
      el.style.zIndex = String(3 - depth);
      el.style.opacity = depth === 2 ? '0.55' : '1';
      const img = document.createElement('img');
      img.decoding = 'async'; img.draggable = false;
      img.width = 600; img.height = 894;
      this.picture(img, it, depth === 0);
      el.append(img);
      return el;
    }

    render() {
      const depths = this.items.length > 2 ? [2, 1, 0] : this.items.length > 1 ? [1, 0] : [0];
      const ssr = this.deck.querySelectorAll('[data-ssr]');
      if (ssr.length === depths.length) {
        // The first cards came with the page: keep them (their pictures are loaded or loading), name the front one.
        ssr.forEach((el) => el.removeAttribute('data-ssr'));
        const it = this.items[this.index];
        this.deck.lastElementChild.querySelector('img').alt = fill(this.text.pair_alt, { title: it.shoe.title, look: it.look });
      } else {
        this.deck.replaceChildren(...depths.map((d) => this.card(this.items[this.mod(this.index + d)], d)));
      }
      const it = this.items[this.index], t = this.text, n = this.items.length;
      const set = (sel, v) => { const el = this.querySelector(sel); if (el.textContent !== v) el.textContent = v; };
      if (this.hasAttribute('data-compact')) {
        // On the shoe's own page: the look leads, with its occasion and its rank for this shoe.
        set('[data-occasion]', it.occasion);
        set('[data-look-name]', it.look);
        set('[data-rank]', it.rank === 0 ? t.rank_first : fill(t.rank_also, { n: it.rank + 1, total: it.of }));
      } else {
        set('[data-shoe-name]', it.shoe.title);
        set('[data-look]', it.rank === 0 ? fill(t.first, { look: it.look }) : fill(t.also, { look: it.look, n: it.rank + 1, total: it.of }));
        set('[data-shoe-price]', it.shoe.price);
        const link = this.querySelector('[data-shoe-link]');
        link.href = it.shoe.url;
        link.setAttribute('aria-label', fill(t.view_shoe_label, { title: it.shoe.title }));
      }
      this.querySelector('[data-count]').innerHTML = `<b>${pad(this.index + 1)}</b> / ${pad(n)}`;
      set('[data-live]', fill(t.live, { n: this.index + 1, total: n, title: it.shoe.title, look: it.look }));
      // The next two pictures are already in the stack; load the previous one too, so a turn left never shows a blank card.
      if (n > 3) this.picture(new Image(), this.items[this.mod(this.index - 1)], false);
    }

    // The front card leaves the way of dir (1 right, next; -1 left, previous) and the card behind rises with the new picture.
    go(dir) {
      if (this.busy || this.items.length < 2) return;
      if (reduce.matches) { this.index = this.mod(this.index + dir); this.render(); return; }
      this.busy = true;
      const cards = [...this.deck.children];
      const front = cards[cards.length - 1], behind = cards[cards.length - 2];
      this.picture(behind.querySelector('img'), this.items[this.mod(this.index + dir)], true);
      front.classList.remove('is-held');
      front.style.transform = `translateX(${dir * 115}%) rotate(${dir * 8}deg)`;
      front.style.opacity = '0';
      behind.style.transform = this.slot(0); behind.style.opacity = '1';
      if (cards.length > 2) { cards[0].style.transform = this.slot(1); cards[0].style.opacity = '1'; }
      this.timer = setTimeout(() => { this.index = this.mod(this.index + dir); this.busy = false; this.render(); }, LEAVE);
    }

    bind() {
      const signal = this.ctrl.signal;
      this.querySelectorAll('[data-dir]').forEach((b) => b.addEventListener('click', () => this.go(Number(b.dataset.dir)), { signal }));
      this.deck.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.go(e.key === 'ArrowRight' ? 1 : -1); }
      }, { signal });

      // A swipe carries the front card with the finger; past 60px, or a quick flick, it turns the deck that way,
      // otherwise the card springs back. Vertical moves stay with the page (touch-action: pan-y).
      let drag = null;
      this.deck.addEventListener('pointerdown', (e) => {
        if (this.busy || e.button !== 0 || this.items.length < 2) return;
        const front = this.deck.lastElementChild;
        drag = { x: e.clientX, t: performance.now(), front, dx: 0 };
        front.classList.add('is-held');
        this.deck.setPointerCapture(e.pointerId);
      }, { signal });
      this.deck.addEventListener('pointermove', (e) => {
        if (!drag) return;
        drag.dx = e.clientX - drag.x;
        drag.front.style.transform = `translateX(${drag.dx}px) rotate(${drag.dx / 40}deg)`;
      }, { signal });
      const end = () => {
        if (!drag) return;
        const { front, dx, t } = drag; drag = null;
        const fast = Math.abs(dx) / Math.max(1, performance.now() - t) > FLICK && Math.abs(dx) >= 20;
        if (Math.abs(dx) > SWIPE || fast) { this.go(dx > 0 ? 1 : -1); return; }
        front.classList.remove('is-held');
        front.style.transform = this.slot(0);
      };
      this.deck.addEventListener('pointerup', end, { signal });
      this.deck.addEventListener('pointercancel', end, { signal });
    }
  }

  customElements.define('mm-pairing-reel', MmPairingReel);
})();
